import { HttpClient, HttpEvent, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ApiResponse } from '../models/api.model';
import {
  ILieferterminOverviewRow,
  IOrderDocument,
  IOrderDocumentFalsePosition,
  IOrderDocumentFile,
  IOrderDocumentPosition,
  IOrderDocumentSummary,
  OrderDocumentStatusEnum,
} from '../models/order-document.model';
import { PaginatedResponse } from './delivery-note.service';

@Injectable({
  providedIn: 'root',
})
export class OrderDocumentService {
  private apiUrl: string = `${environment.apiUrl}/order-document`;

  constructor(private http: HttpClient) { }

  /**
   * Get paginated order documents with search and sorting
   */
  public getAll(
    page: number = 1,
    pageSize: number = 10,
    search: string = '',
    sortField: string = 'createdAt',
    sortDirection: string = 'desc',
    statusFilter: string = ''
  ): Observable<ApiResponse<PaginatedResponse<IOrderDocument>>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString())
      .set('sortField', sortField)
      .set('sortDirection', sortDirection);

    if (search) {
      params = params.set('search', search);
    }

    if (statusFilter) {
      params = params.set('statusFilter', statusFilter);
    }

    return this.http.get<ApiResponse<PaginatedResponse<IOrderDocument>>>(`${this.apiUrl}`, { params });
  }

  /**
   * Today's Liefertermine for the production TV overview board (public endpoint, no auth).
   * Returns a flat list of today's delivery dates, sorted sicher-first. `werkFilter` is a
   * comma-separated list of WerkType values; empty means all Werke.
   */
  public getTodaysLiefertermine(werkFilter: string = ''): Observable<ApiResponse<ILieferterminOverviewRow[]>> {
    let params = new HttpParams();

    if (werkFilter) {
      params = params.set('werkFilter', werkFilter);
    }

    return this.http.get<ApiResponse<ILieferterminOverviewRow[]>>(`${environment.apiUrl}/liefertermine-overview`, { params });
  }

  /**
   * Paginated Liefertermine from today onwards for the in-app Liefertermine page. Same row shape as
   * the TV board, but login-gated and filtered/sorted/paged on the server.
   */
  public getUpcomingLiefertermine(
    page: number = 1,
    pageSize: number = 10,
    search: string = '',
    sortField: string = 'isBinding',
    sortDirection: string = 'desc',
    werkFilter: string = ''
  ): Observable<ApiResponse<PaginatedResponse<ILieferterminOverviewRow>>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString())
      .set('sortField', sortField)
      .set('sortDirection', sortDirection);

    if (search) {
      params = params.set('search', search);
    }

    if (werkFilter) {
      params = params.set('werkFilter', werkFilter);
    }

    return this.http.get<ApiResponse<PaginatedResponse<ILieferterminOverviewRow>>>(`${environment.apiUrl}/liefertermine`, {
      params,
    });
  }

  /** All Auftragsbegleitscheine linked to one order (order._id), oldest first. */
  public getByOrderId(orderId: string): Observable<ApiResponse<IOrderDocumentSummary[]>> {
    return this.http.get<ApiResponse<IOrderDocumentSummary[]>>(`${this.apiUrl}/order/${encodeURIComponent(orderId)}`);
  }

  /**
   * Get a specific order document by ID
   */
  public getById(id: string): Observable<ApiResponse<IOrderDocument>> {
    return this.http.get<ApiResponse<IOrderDocument>>(`${this.apiUrl}/${id}`);
  }

  /**
   * Create a new order document
   */
  public create(orderDocument: Partial<IOrderDocument>): Observable<ApiResponse<IOrderDocument>> {
    return this.http.post<ApiResponse<IOrderDocument>>(this.apiUrl, orderDocument);
  }

  /**
   * Update an order document
   */
  public update(id: string, orderDocument: Partial<IOrderDocument>): Observable<ApiResponse<IOrderDocument>> {
    return this.http.put<ApiResponse<IOrderDocument>>(`${this.apiUrl}/${id}`, orderDocument).pipe(
      catchError(error => {
        console.error(`Error updating order document ${id}:`, error);
        return throwError(() => new Error('Failed to update order document'));
      })
    );
  }

  /**
   * Delete an order document
   */
  public delete(id: string): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.apiUrl}/${id}`);
  }

  /**
   * Update order document status (quick status change)
   */
  public updateStatus(id: string, status: OrderDocumentStatusEnum): Observable<ApiResponse<IOrderDocument>> {
    return this.http.patch<ApiResponse<IOrderDocument>>(`${this.apiUrl}/${id}/status`, { status });
  }

  /**
   * Add a position (work-log entry) to an order document
   */
  public addPosition(id: string, position: Partial<IOrderDocumentPosition>): Observable<ApiResponse<IOrderDocument>> {
    return this.http.post<ApiResponse<IOrderDocument>>(`${this.apiUrl}/${id}/positions`, position).pipe(
      catchError(error => {
        console.error(`Error adding position to order document ${id}:`, error);
        return throwError(() => new Error('Failed to add position'));
      })
    );
  }

  /**
   * Update a position (work-log entry) on an order document
   */
  public updatePosition(
    id: string,
    positionId: string,
    position: Partial<IOrderDocumentPosition>
  ): Observable<ApiResponse<IOrderDocument>> {
    return this.http.put<ApiResponse<IOrderDocument>>(`${this.apiUrl}/${id}/positions/${positionId}`, position).pipe(
      catchError(error => {
        console.error(`Error updating position ${positionId} on order document ${id}:`, error);
        return throwError(() => new Error('Failed to update position'));
      })
    );
  }

  /**
   * Delete a position (work-log entry) from an order document
   */
  public deletePosition(id: string, positionId: string): Observable<ApiResponse<IOrderDocument>> {
    return this.http.delete<ApiResponse<IOrderDocument>>(`${this.apiUrl}/${id}/positions/${positionId}`).pipe(
      catchError(error => {
        console.error(`Error deleting position ${positionId} on order document ${id}:`, error);
        return throwError(() => new Error('Failed to delete position'));
      })
    );
  }

  /**
   * Add a false position (Fehlererfassung entry) to an order document
   */
  public addFalsePosition(id: string, falsePosition: IOrderDocumentFalsePosition): Observable<ApiResponse<IOrderDocument>> {
    return this.http.post<ApiResponse<IOrderDocument>>(`${this.apiUrl}/${id}/false-positions`, falsePosition).pipe(
      catchError(error => {
        console.error(`Error adding false position to order document ${id}:`, error);
        return throwError(() => new Error('Failed to add false position'));
      })
    );
  }

  public updateFalsePosition(
    id: string,
    falsePositionId: string,
    falsePosition: IOrderDocumentFalsePosition
  ): Observable<ApiResponse<IOrderDocument>> {
    return this.http.put<ApiResponse<IOrderDocument>>(`${this.apiUrl}/${id}/false-positions/${falsePositionId}`, falsePosition).pipe(
      catchError(error => {
        console.error(`Error updating false position ${falsePositionId} on order document ${id}:`, error);
        return throwError(() => new Error('Failed to update false position'));
      })
    );
  }

  public deleteFalsePosition(id: string, falsePositionId: string): Observable<ApiResponse<IOrderDocument>> {
    return this.http.delete<ApiResponse<IOrderDocument>>(`${this.apiUrl}/${id}/false-positions/${falsePositionId}`).pipe(
      catchError(error => {
        console.error(`Error deleting false position ${falsePositionId} on order document ${id}:`, error);
        return throwError(() => new Error('Failed to delete false position'));
      })
    );
  }

  // Documents (Dokumente tab). Errors are passed through untouched: the caller maps the HTTP status
  // (413 too large, 423 locked after DMS export, 409 export running, …) to a message.

  public getDocuments(id: string): Observable<ApiResponse<IOrderDocumentFile[]>> {
    return this.http.get<ApiResponse<IOrderDocumentFile[]>>(`${this.apiUrl}/${id}/documents`);
  }

  /** The documents of every Auftragsbegleitschein linked to one order, newest first. */
  public getDocumentsByOrderId(orderId: string): Observable<ApiResponse<IOrderDocumentFile[]>> {
    return this.http.get<ApiResponse<IOrderDocumentFile[]>>(`${this.apiUrl}/order/${encodeURIComponent(orderId)}/documents`);
  }

  /** One file per request, with upload progress events. The browser sets the multipart boundary. */
  public uploadDocument(id: string, file: File): Observable<HttpEvent<ApiResponse<IOrderDocumentFile[]>>> {
    // Some systems report no MIME type for .pdf files; the backend checks the content anyway.
    const pdf = file.type === 'application/pdf' ? file : new File([file], file.name, { type: 'application/pdf' });
    const body = new FormData();
    body.append('files', pdf, file.name);
    return this.http.post<ApiResponse<IOrderDocumentFile[]>>(`${this.apiUrl}/${id}/documents`, body, {
      reportProgress: true,
      observe: 'events',
    });
  }

  /** The PDF as Blob — fetched via HttpClient because the auth token travels in a header. */
  public getDocumentBlob(id: string, fileId: string): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/${id}/documents/${fileId}`, { responseType: 'blob' });
  }

  public deleteDocument(id: string, fileId: string): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.apiUrl}/${id}/documents/${fileId}`);
  }
}
